import { useEffect, useMemo, useRef, useState } from "react"

import { ZapIcon } from "./icons"
import { cursorImageUrl, cursorRule } from "./TryZone.helpers"
import { playbackSequence } from "../services/playbackSequence"

import type { Point } from "../../shared/types"
import type { Frame } from "../types/Frame"
import type { Playback } from "../types/Playback"
import type { PreviewBackground } from "../types/PreviewBackground"
import type { ResolvedResampling } from "../types/Resampling"
import type { PointerEvent } from "react"

const ZONE_ID = "cursor-converter-try-zone"
const SIZES = [32, 48, 64]

interface Pulse {
  id: number
  x: number
  y: number
}

interface Props {
  frames: Frame[]
  playback: Playback
  delayMs: number
  hotspot: Point
  resampling: ResolvedResampling
  previewBackground: PreviewBackground
}

export const TryZone = ({
  frames,
  playback,
  delayMs,
  hotspot,
  resampling,
  previewBackground,
}: Props) => {
  const [size, setSize] = useState(SIZES[0])
  // Its own clock, so pausing the preview doesn't freeze the real cursor.
  const [step, setStep] = useState(0)
  const [pulses, setPulses] = useState<Pulse[]>([])
  const [isTrailFading, setIsTrailFading] = useState(false)
  const trailRef = useRef<HTMLCanvasElement>(null)
  const isDrawingRef = useRef(false)
  const nextPulseIdRef = useRef(0)
  const isAnimated = frames.length > 1
  const sequence = playbackSequence(frames.length, playback)
  const urls = useMemo(
    () => frames.map((frame) => cursorImageUrl(frame.pixels, size, resampling)),
    [frames, size, resampling],
  )
  const url = urls[sequence[step % sequence.length]]

  useEffect(() => {
    if (!isAnimated) return

    const timer = window.setInterval(() => setStep((current) => current + 1), delayMs)
    return () => window.clearInterval(timer)
  }, [isAnimated, delayMs])

  // Pointer position inside the zone's padding box, where the trail canvas and pulses live.
  const pointFrom = (event: PointerEvent<HTMLDivElement>) => {
    const zone = event.currentTarget
    const rect = zone.getBoundingClientRect()
    return {
      x: event.clientX - rect.left - zone.clientLeft,
      y: event.clientY - rect.top - zone.clientTop,
    }
  }

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return

    const zone = event.currentTarget
    const { x, y } = pointFrom(event)
    nextPulseIdRef.current += 1
    const id = nextPulseIdRef.current
    setPulses((current) => [...current, { id, x, y }])

    const canvas = trailRef.current
    const context = canvas?.getContext("2d")
    if (!canvas || !context) return

    canvas.width = zone.clientWidth
    canvas.height = zone.clientHeight
    context.lineWidth = 3
    context.lineCap = "round"
    context.lineJoin = "round"
    context.strokeStyle = getComputedStyle(zone).getPropertyValue("--color-accent")
    context.beginPath()
    context.moveTo(x, y)
    isDrawingRef.current = true
    setIsTrailFading(false)
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const context = trailRef.current?.getContext("2d")
    if (!isDrawingRef.current || !context) return

    const { x, y } = pointFrom(event)
    context.lineTo(x, y)
    context.stroke()
  }

  const endTrail = () => {
    if (!isDrawingRef.current) return

    isDrawingRef.current = false
    setIsTrailFading(true)
  }

  return (
    <div
      id={ZONE_ID}
      data-theme={previewBackground === "transparent" ? undefined : previewBackground}
      className={`relative flex min-h-60 flex-1 flex-col items-center justify-center gap-3 overflow-hidden rounded-box border-2 border-dashed border-base-content/20 p-4 text-center text-base-content transition-colors select-none hover:border-primary/60 ${
        previewBackground === "transparent"
          ? "bg-base-300 bg-linear-135 from-primary/8 to-transparent to-60%"
          : "bg-base-100"
      }`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endTrail}
      onPointerLeave={endTrail}
      onPointerCancel={endTrail}
    >
      <style>{cursorRule(ZONE_ID, url, hotspot, size)}</style>
      <canvas
        ref={trailRef}
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 size-full transition-opacity duration-800 ${
          isTrailFading ? "opacity-0" : "opacity-90"
        }`}
        onTransitionEnd={(event) => {
          if (!isTrailFading) return

          const canvas = event.currentTarget
          canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height)
          setIsTrailFading(false)
        }}
      />

      <div className="pointer-events-none flex max-w-xs flex-col gap-1">
        <strong className="font-display">Try it here</strong>
        <span className="text-xs leading-relaxed text-muted-content">
          This is the real cursor. Click anywhere — the pulse marks the hotspot. Hold and drag to
          draw.
        </span>
      </div>

      <button
        type="button"
        className="btn h-11 gap-2 rounded-full border-2 border-base-content/10 bg-neutral px-6 font-extrabold text-base-content hover:border-primary hover:bg-primary hover:text-primary-content"
      >
        <ZapIcon className="size-4.5" />
        Hover me
      </button>

      <div
        role="group"
        aria-label="Cursor size"
        className="mt-2 flex items-center gap-2 text-xs font-bold text-muted-content"
      >
        <span aria-hidden="true">Cursor size</span>
        {SIZES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-pressed={size === candidate}
            onClick={() => setSize(candidate)}
            className={`btn rounded-full px-2.5 font-bold btn-xs ${
              size === candidate
                ? "btn-soft btn-primary"
                : "border-base-content/10 bg-base-200 text-muted-content"
            }`}
          >
            {candidate}
          </button>
        ))}
      </div>

      {pulses.map((pulse) => (
        <span
          key={pulse.id}
          aria-hidden="true"
          className="pointer-events-none absolute size-3.5 -translate-1/2 animate-pulse-ring rounded-full border-2 border-accent bg-accent/40"
          style={{ left: pulse.x, top: pulse.y }}
          onAnimationEnd={() => setPulses((current) => current.filter(({ id }) => id !== pulse.id))}
        />
      ))}
    </div>
  )
}
